// Route table for the public site, protected app shell, and role-gated admin surfaces.
import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedRoute } from '@/ui/auth/ProtectedRoute';
import { PublicAuthCallbackBoundary } from '@/ui/auth/PublicAuthCallbackBoundary';
import { PageLoader } from '@/ui/PageLoader';

// -- Public routes (eager) --
import Index from '@/pages/Index';
import NotFound from '@/pages/NotFound';

// -- Auth routes (lazy) --
const SignIn = React.lazy(() => import('@/pages/SignIn'));
const SignUp = React.lazy(() => import('@/pages/SignUp'));
const AuthCallback = React.lazy(() => import('@/pages/AuthCallback'));
const ForgotPassword = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const VerifyEmailPending = React.lazy(() => import('@/pages/VerifyEmailPending'));
const LandingPrototypeHub = React.lazy(() =>
  import('@/pages/landing-prototypes/LandingPrototypePage').then((module) => ({
    default: module.LandingPrototypeHubPage,
  })),
);
const LandingPlayerFirstPrototype = React.lazy(() =>
  import('@/pages/landing-prototypes/LandingPrototypePage').then((module) => ({
    default: module.PlayerFirstMinimalPage,
  })),
);
const LandingBusinessLanePrototype = React.lazy(() =>
  import('@/pages/landing-prototypes/LandingPrototypePage').then((module) => ({
    default: module.PlayerHeroWithBusinessLanePage,
  })),
);
const LandingIntentChipsPrototype = React.lazy(() =>
  import('@/pages/landing-prototypes/LandingPrototypePage').then((module) => ({
    default: module.IntentChipsPage,
  })),
);

// -- User routes (lazy) --
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Book = React.lazy(() => import('@/pages/Book'));
const DiscoveryPrototypeHub = React.lazy(() =>
  import('@/pages/book-discovery/DiscoveryPrototypePage').then((module) => ({
    default: module.DiscoveryPrototypeHubPage,
  })),
);
const SportFirstPrototype = React.lazy(() =>
  import('@/pages/book-discovery/DiscoveryPrototypePage').then((module) => ({
    default: module.SportFirstPrototypePage,
  })),
);
const AvailabilityFirstPrototype = React.lazy(() =>
  import('@/pages/book-discovery/DiscoveryPrototypePage').then((module) => ({
    default: module.AvailabilityFirstPrototypePage,
  })),
);
const SplitDiscoveryPrototype = React.lazy(() =>
  import('@/pages/book-discovery/DiscoveryPrototypePage').then((module) => ({
    default: module.SplitDiscoveryPrototypePage,
  })),
);
const DevRoutes = import.meta.env.VITE_APP_ENV === 'dev' ? React.lazy(() => import('@/app/dev/DevRoutes')) : null;
const FacilityBookingPage = React.lazy(() => import('@/pages/FacilityBooking'));
const MyBookings = React.lazy(() => import('@/pages/MyBookings'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const RegisterBusiness = React.lazy(() => import('@/pages/RegisterBusiness'));
const RegistrationResumeChoice = React.lazy(() => import('@/pages/RegistrationResumeChoice'));
const Pricing = React.lazy(() => import('@/pages/Pricing'));
const ManageBusinesses = React.lazy(() => import('@/pages/ManageBusinesses'));

// -- Admin routes (lazy) --
const BusinessesManagement = React.lazy(() => import('@/pages/admin/BusinessesManagement'));
const UsersManagement = React.lazy(() => import('@/pages/admin/UsersManagement'));
const SportsManagement = React.lazy(() => import('@/pages/admin/SportsManagement'));
const BusinessDashboard = React.lazy(() => import('@/pages/admin/BusinessDashboard'));
const BookingsManagement = React.lazy(() => import('@/pages/admin/BookingsManagement'));
const ActivityLogs = React.lazy(() => import('@/pages/admin/ActivityLogs'));
const PricingManagement = React.lazy(() => import('@/pages/admin/PricingManagement'));

// Keep public-route loading free of dashboard chrome during lazy page transitions.
const LazyPublic: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PublicAuthCallbackBoundary>
    <Suspense fallback={<PageLoader withLayout={false} />}>{children}</Suspense>
  </PublicAuthCallbackBoundary>
);

// Keep the dashboard shell visible only for authenticated route transitions.
const LazyProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

export const AppRoutes: React.FC = () => (
  <Routes>
    {/* Public */}
    <Route path="/" element={<PublicAuthCallbackBoundary><Index /></PublicAuthCallbackBoundary>} />
    <Route path="/signin" element={<LazyPublic><SignIn /></LazyPublic>} />
    <Route path="/signup" element={<LazyPublic><SignUp /></LazyPublic>} />
    <Route path="/forgot-password" element={<LazyPublic><ForgotPassword /></LazyPublic>} />
    <Route path="/reset-password" element={<LazyPublic><ResetPassword /></LazyPublic>} />
    <Route path="/verify-email-pending" element={<LazyPublic><VerifyEmailPending /></LazyPublic>} />
    <Route path="/register-business" element={<LazyPublic><RegisterBusiness /></LazyPublic>} />
    <Route path="/registration-resume" element={<ProtectedRoute><LazyProtected><RegistrationResumeChoice /></LazyProtected></ProtectedRoute>} />
    <Route path="/pricing" element={<LazyPublic><Pricing /></LazyPublic>} />
    <Route path="/auth/callback" element={<LazyPublic><AuthCallback /></LazyPublic>} />
    <Route path="/landing/prototypes" element={<LazyPublic><LandingPrototypeHub /></LazyPublic>} />
    <Route path="/landing/prototypes/player-first" element={<LazyPublic><LandingPlayerFirstPrototype /></LazyPublic>} />
    <Route path="/landing/prototypes/business-lane" element={<LazyPublic><LandingBusinessLanePrototype /></LazyPublic>} />
    <Route path="/landing/prototypes/intent-chips" element={<LazyPublic><LandingIntentChipsPrototype /></LazyPublic>} />

    {/* User (protected) */}
    <Route path="/dashboard" element={<ProtectedRoute><LazyProtected><Dashboard /></LazyProtected></ProtectedRoute>} />
    <Route path="/book" element={<ProtectedRoute><LazyProtected><Book /></LazyProtected></ProtectedRoute>} />
    <Route path="/book/prototypes" element={<ProtectedRoute><LazyProtected><DiscoveryPrototypeHub /></LazyProtected></ProtectedRoute>} />
    <Route path="/book/prototypes/sport-first" element={<ProtectedRoute><LazyProtected><SportFirstPrototype /></LazyProtected></ProtectedRoute>} />
    <Route path="/book/prototypes/availability-first" element={<ProtectedRoute><LazyProtected><AvailabilityFirstPrototype /></LazyProtected></ProtectedRoute>} />
    <Route path="/book/prototypes/split" element={<ProtectedRoute><LazyProtected><SplitDiscoveryPrototype /></LazyProtected></ProtectedRoute>} />
    {DevRoutes ? (
      <Route
        path="/dev/*"
        element={
          <ProtectedRoute>
            <LazyProtected>
              <DevRoutes />
            </LazyProtected>
          </ProtectedRoute>
        }
      />
    ) : null}
    <Route path="/book/:slug" element={<ProtectedRoute><LazyProtected><FacilityBookingPage /></LazyProtected></ProtectedRoute>} />
    <Route path="/bookings" element={<ProtectedRoute><LazyProtected><MyBookings /></LazyProtected></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><LazyProtected><Settings /></LazyProtected></ProtectedRoute>} />
    <Route path="/manage-businesses" element={<ProtectedRoute requiredRole="business_admin"><LazyProtected><ManageBusinesses /></LazyProtected></ProtectedRoute>} />

    {/* Super Admin */}
    <Route path="/admin/businesses" element={<ProtectedRoute requiredRole="super_admin"><LazyProtected><BusinessesManagement /></LazyProtected></ProtectedRoute>} />
    <Route path="/admin/pricing" element={<ProtectedRoute requiredRole="super_admin"><LazyProtected><PricingManagement /></LazyProtected></ProtectedRoute>} />

    {/* Business Admin+ */}
    <Route path="/admin/users" element={<ProtectedRoute requiredRole="business_admin"><LazyProtected><UsersManagement /></LazyProtected></ProtectedRoute>} />
    <Route path="/admin/sports" element={<ProtectedRoute requiredRole="business_admin"><LazyProtected><SportsManagement /></LazyProtected></ProtectedRoute>} />
    <Route path="/admin/business" element={<ProtectedRoute requiredRole="business_admin"><LazyProtected><BusinessDashboard /></LazyProtected></ProtectedRoute>} />

    {/* Staff+ */}
    <Route path="/admin/bookings" element={<ProtectedRoute requiredRole="staff"><LazyProtected><BookingsManagement /></LazyProtected></ProtectedRoute>} />
    <Route path="/admin/activity" element={<ProtectedRoute requiredRole="staff"><LazyProtected><ActivityLogs /></LazyProtected></ProtectedRoute>} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);



